import type { AgeCategory } from '../../types/tax';
import { TaxDataManager } from '../taxYear/TaxDataManager';

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export function validateGrossIncome(grossIncome: number): ValidationResult {
  if (isNaN(grossIncome) || grossIncome < 0) {
    return {
      isValid: false,
      message: 'Gross income must be a positive number'
    };
  }
  
  if (grossIncome === 0) {
    return {
      isValid: false,
      message: 'Gross income must be greater than zero'
    };
  }
  
  // Check for reasonable upper bound (R50 million annually)
  if (grossIncome > 50000000) {
    return {
      isValid: false,
      message: 'Gross income exceeds reasonable limit (R50 million)'
    };
  }
  
  return { isValid: true };
}

export function validateNetIncome(netIncome: number): ValidationResult {
  if (isNaN(netIncome) || netIncome < 0) {
    return {
      isValid: false,
      message: 'Net income must be a positive number'
    };
  }
  
  if (netIncome === 0) {
    return {
      isValid: false,
      message: 'Net income must be greater than zero'
    };
  }
  
  // Check for reasonable upper bound (considering maximum possible net income)
  if (netIncome > 25000000) {
    return {
      isValid: false,
      message: 'Net income exceeds reasonable limit (R25 million)'
    };
  }
  
  return { isValid: true };
}

export function validateAgeCategory(ageCategory: string): ValidationResult {
  const validCategories: AgeCategory[] = ['under65', 'age65to74', 'age75plus'];
  
  if (!validCategories.includes(ageCategory as AgeCategory)) {
    return {
      isValid: false,
      message: 'Age category must be one of: under65, age65to74, age75plus'
    };
  }
  
  return { isValid: true };
}

export function validateIncomeRange(income: number, minIncome = 1000, maxIncome = 10000000): ValidationResult {
  if (income < minIncome) {
    return {
      isValid: false,
      message: `Income must be at least R${minIncome.toLocaleString()}`
    };
  }
  
  if (income > maxIncome) {
    return {
      isValid: false,
      message: `Income cannot exceed R${maxIncome.toLocaleString()}`
    };
  }
  
  return { isValid: true };
}

export function isAboveTaxThreshold(income: number, ageCategory: AgeCategory): boolean {
  const taxManager = TaxDataManager.getInstance();
  const thresholds = taxManager.getTaxThresholds();
  const threshold = thresholds[ageCategory];
  return income > threshold;
}

export function getRecommendedIncomeRange(ageCategory: AgeCategory): { min: number; max: number } {
  const taxManager = TaxDataManager.getInstance();
  const thresholds = taxManager.getTaxThresholds();
  const threshold = thresholds[ageCategory];
  
  return {
    min: Math.max(1000, threshold - 50000), // At least R1,000 or just below threshold
    max: 10000000 // Reasonable upper limit
  };
}